import { FC, ReactNode, useContext } from "react";
import { Spinner } from "@justworkshr/milo-core";
import useWindowSize from "../hooks/useWindowSize";
import Stepper from "../Stepper";
import Step from "../Step";
import {
  EoROnboardingSteps,
  type StepperStatus,
  type EoROnboardingStep,
} from "./constants";

import styles from "./Layout.module.css";
import OnboardingHeader from "./OnboardingHeader";
import { TaxInfoConfig } from "./tax-info/TaxInfoForm/tax-info-config";
import { EoRMemberContext } from "../contexts/eorMemberContext";
import { OnboardingContext } from "./contexts/onboardingContext";

const { centered, content, layout, padding, fullWidth, hidden, stepper } =
  styles;

type Props = {
  step: number;
  loading: boolean;
  idVerificationStatus?: StepperStatus;
  backgroundCheckStatus?: StepperStatus;
  children: ReactNode;
};

const getCountrySpecificSteps = (
  countryCode: string,
  allSteps: EoROnboardingStep[],
  hasBackgroundCheck?: boolean
) => {
  const requiredSteps = allSteps.filter((step) => {
    const baseSteps = [
      "profile-info",
      "contact-info",
      "id-verification",
      "payment-info",
    ];
    // Only include background-check if hasBackgroundCheck is true
    if (step.key === "background-check") {
      return hasBackgroundCheck === true;
    }
    return baseSteps.includes(step.key);
  });
  if (countryCode in TaxInfoConfig) {
    const taxStep = allSteps.find((step) => step.key === "tax-info");
    if (taxStep) requiredSteps.push(taxStep);
  }
  return requiredSteps;
};

const Layout: FC<Props> = ({
  step,
  loading,
  idVerificationStatus,
  backgroundCheckStatus,
  children,
}) => {
  const { width } = useWindowSize();

  const eorMemberContext = useContext(EoRMemberContext);
  const onboardingContext = useContext(OnboardingContext);
  const workCountry = eorMemberContext?.workCountry;
  const steps = getCountrySpecificSteps(
    workCountry ?? "",
    EoROnboardingSteps,
    onboardingContext?.hasBackgroundCheck
  );

  return (
    <>
      <OnboardingHeader />
      <div
        className={["milo--grid", padding, step < 1 ? centered : layout].join(
          " "
        )}
      >
        <aside className={step < 1 ? hidden : stepper}>
          <Stepper
            direction={width > 850 ? "vertical" : "horizontal"}
            hideStepLabels={width <= 850}
          >
            {steps.map(({ key, title, status }, index) => (
              <Step
                key={key}
                title={title}
                stepNumber={index + 1}
                status={status(
                  index + 1,
                  step,
                  key === "id-verification"
                    ? idVerificationStatus
                    : key === "background-check"
                    ? backgroundCheckStatus
                    : undefined
                )}
              />
            ))}
          </Stepper>
        </aside>

        <section className={step < 1 ? fullWidth : content}>
          {loading ? <Spinner /> : children}
        </section>
      </div>
    </>
  );
};

Layout.defaultProps = {
  idVerificationStatus: undefined,
  backgroundCheckStatus: undefined,
};

export default Layout;
